import {
  Directive,
  Input,
  OnDestroy,
  TemplateRef,
  ViewContainerRef,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { PermissionService } from '../services/permission.service';

/** Attribute directive (hide/disable/mask) — re-exported for convenience. */
export { AcanPermissionDirective } from './acan-permission.directive';

/**
 * Structural directive to show/hide elements based on permission.
 * Usage: *hasPermission="'CLIENT_ADD'" or *hasPermission="'CLIENT_ADD'; else noAccess"
 * Use hasAnyPermission for multiple: *hasAnyPermission="['CLIENT_ADD','CLIENT_EDIT_FULL']"
 */
@Directive({
  selector: '[hasPermission]',
  standalone: true,
})
export class HasPermissionDirective implements OnDestroy {
  private permissionCode: string | null = null;
  private hasView = false;
  private sub?: Subscription;

  constructor(
    private templateRef: TemplateRef<unknown>,
    private viewContainer: ViewContainerRef,
    private permissionService: PermissionService
  ) {
    this.sub = this.permissionService.permissionsChanged$.subscribe(() =>
      this.updateView()
    );
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  @Input() set hasPermission(permissionCode: string | string[] | null) {
    const code = Array.isArray(permissionCode)
      ? permissionCode.length > 0
        ? permissionCode[0]
        : null
      : permissionCode;
    this.permissionCode = code ?? null;
    this.updateView();
  }

  private updateView(): void {
    const allowed =
      this.permissionCode != null &&
      this.permissionService.hasPermission(this.permissionCode);
    if (allowed && !this.hasView) {
      this.viewContainer.createEmbeddedView(this.templateRef);
      this.hasView = true;
    } else if (!allowed && this.hasView) {
      this.viewContainer.clear();
      this.hasView = false;
    }
  }
}

/**
 * Show element if user has ANY of the given permissions.
 * Usage: *hasAnyPermission="['CLIENT_ADD','CLIENT_EDIT_FULL']"
 */
@Directive({
  selector: '[hasAnyPermission]',
  standalone: true,
})
export class HasAnyPermissionDirective implements OnDestroy {
  private permissionCodes: string[] = [];
  private hasView = false;
  private sub?: Subscription;

  constructor(
    private templateRef: TemplateRef<unknown>,
    private viewContainer: ViewContainerRef,
    private permissionService: PermissionService
  ) {
    this.sub = this.permissionService.permissionsChanged$.subscribe(() =>
      this.updateView()
    );
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
  }

  @Input() set hasAnyPermission(permissionCodes: string | string[] | null) {
    if (Array.isArray(permissionCodes)) {
      this.permissionCodes = permissionCodes;
    } else if (typeof permissionCodes === 'string') {
      this.permissionCodes = permissionCodes ? [permissionCodes] : [];
    } else {
      this.permissionCodes = [];
    }
    this.updateView();
  }

  private updateView(): void {
    const allowed =
      this.permissionCodes.length > 0 &&
      this.permissionService.hasAnyPermission(this.permissionCodes);
    if (allowed && !this.hasView) {
      this.viewContainer.createEmbeddedView(this.templateRef);
      this.hasView = true;
    } else if (!allowed && this.hasView) {
      this.viewContainer.clear();
      this.hasView = false;
    }
  }
}
